import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuthStore } from '@/stores/authStore';
import { logger } from '@/utils/logger';

import { useDeleteProfile } from '../hooks/useProfile';

interface DangerZoneCardProps {
  hasActiveSubscription?: boolean;
  subscriptionPlatform?: 'stripe' | 'apple';
}

export default function DangerZoneCard({
  hasActiveSubscription,
  subscriptionPlatform,
}: DangerZoneCardProps) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const deleteProfile = useDeleteProfile();
  const { logout } = useAuthStore();
  const navigate = useNavigate();

  const canDelete = confirmText.toLowerCase() === 'delete my account';

  const handleDelete = async () => {
    if (!canDelete) return;

    try {
      await deleteProfile.mutateAsync();
      await logout();
      // Clear any remaining local storage
      localStorage.clear();
      navigate('/login');
    } catch (err) {
      logger.error('Failed to delete account:', err);
    }
  };

  return (
    <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-6">
      <h2 className="text-lg font-semibold text-red-400 mb-4 flex items-center gap-2">
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
        Danger Zone
      </h2>

      {!showConfirm ? (
        <div>
          <p className="text-purple-300/80 mb-4">
            Permanently delete your account and all associated data. This action cannot be undone.
          </p>
          <button
            onClick={() => setShowConfirm(true)}
            className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors"
          >
            Delete Account
          </button>
        </div>
      ) : (
        <div>
          <p className="text-white mb-4">This will permanently delete your account, including:</p>
          <ul className="list-disc list-inside text-purple-300/80 mb-4 space-y-1">
            <li>Your profile and preferences</li>
            <li>All tokens — purchased and subscription — will be permanently forfeited</li>
          </ul>
          {hasActiveSubscription && (
            <p className="text-yellow-400/90 text-sm italic mb-4">
              {(() => {
                if (subscriptionPlatform === 'stripe') {
                  return 'Your subscription will be canceled immediately.';
                }
                if (subscriptionPlatform === 'apple') {
                  return 'Your Apple subscription will not be automatically canceled. Please cancel it in Settings \u2192 Subscriptions to avoid continued billing.';
                }
                return 'You have an active subscription that may be affected by account deletion.';
              })()}
            </p>
          )}
          <p className="text-sm text-purple-300 mb-4">
            Type <span className="font-mono text-white">delete my account</span> to confirm:
          </p>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            className="w-full px-3 py-2 mb-4 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            placeholder="delete my account"
          />
          <div className="flex gap-3">
            <button
              onClick={handleDelete}
              disabled={!canDelete || deleteProfile.isPending}
              className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {deleteProfile.isPending ? 'Deleting...' : 'Delete My Account'}
            </button>
            <button
              onClick={() => {
                setShowConfirm(false);
                setConfirmText('');
              }}
              className="px-4 py-2 text-purple-300 hover:text-white transition-colors"
            >
              Cancel
            </button>
          </div>
          {deleteProfile.isError && (
            <p className="mt-4 text-sm text-red-400">Failed to delete account. Please try again.</p>
          )}
        </div>
      )}
    </div>
  );
}
